import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldCheck, Stethoscope, Truck, LayoutDashboard, Send, LineChart, Lock,
  User, Building2, Eye, EyeOff, Loader2, HelpCircle, Mail,
} from 'lucide-react';
import { AuthContext } from '../../context/AuthContext';
import { LOGIN_URL } from '../../constants/api';
import useApiMethods from '../../lib/useApiMethods';
import ToastMsg from '../../components/common/ToastMsg';

const ORG_SLUG_KEY = 'partner_portal_last_org_slug';

const BENEFITS = [
  { icon: LayoutDashboard, title: 'Live dashboard',   text: 'Real-time view of your referrals, conversions and pending incentives.' },
  { icon: Send,            title: 'One-tap referral', text: 'Send a patient to the hospital in under 30 seconds.' },
  { icon: LineChart,       title: 'Transparent payouts', text: 'Track exactly what you\'ve earned and what\'s pending — always up to date.' },
  { icon: Lock,            title: 'Private to you',   text: 'You only see patients you referred. Everything else stays hidden.' },
];


const PartnerPortalSignin = () => {
  const navigate = useNavigate();
  const { loginauth, isAuthenticated } = useContext(AuthContext);
  const { create, fetch: apiFetch } = useApiMethods();
  const orgInputRef = useRef(null);
  const usernameInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [values, setValues] = useState({
    org_slug: '',
    username: '',
    password: '',
  });
  const [errors, setErrors] = useState({});

  // Prefill last-used org for returning partners
  useEffect(() => {
    const stored = localStorage.getItem(ORG_SLUG_KEY) || '';
    if (stored) {
      setValues(v => ({ ...v, org_slug: stored }));
      // Focus straight to username so returning users can log in without editing org
      setTimeout(() => usernameInputRef.current?.focus(), 40);
    } else {
      setTimeout(() => orgInputRef.current?.focus(), 40);
    }
  }, []);

  // Bounce already-signed-in partners
  useEffect(() => {
    if (isAuthenticated && sessionStorage.getItem('token')) {
      const role = sessionStorage.getItem('login_role');
      if (role === 'third_party') navigate('/partner-portal/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const set = (k, v) => {
    setValues(prev => ({ ...prev, [k]: v }));
    if (errors[k]) setErrors(prev => ({ ...prev, [k]: '' }));
  };

  const validate = () => {
    const next = {};
    if (!values.org_slug.trim()) next.org_slug = 'Organisation code is required';
    if (!values.username.trim()) next.username = 'Username is required';
    if (!values.password)        next.password = 'Password is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading || !validate()) return;
    try {
      const resp = await create(LOGIN_URL, {
        user_name: values.username.trim(),
        password: values.password,
        org_slug: values.org_slug.trim(),
      }, false, setLoading);

      if (resp?.status !== 'success') {
        const msg = resp?.message || 'Login failed';
        // Backend returns a single message; surface it inline against
        // password (most common cause) and also as a toast.
        setErrors({ password: msg });
        ToastMsg('error', msg);
        return;
      }
      if (resp.data?.login_role !== 'third_party') {
        const msg = 'This portal is only for referring partners. Use the main hospital login instead.';
        setErrors({ username: 'Not a partner account' });
        ToastMsg('error', msg);
        return;
      }

      await loginauth(resp.data, false);
      localStorage.setItem(ORG_SLUG_KEY, values.org_slug.trim());

      // Confirm the LoginUser is actually linked to a partner profile before
      // redirecting — mirrors the layout's own gate to give a nicer error.
      const who = await apiFetch('engage/portal/whoami/');
      const kind = who?.data?.kind;
      if (!kind) {
        ToastMsg('error', 'Your account is not linked to any partner. Contact the hospital admin.');
        return;
      }
      ToastMsg('success', `Welcome, ${resp.data.profile?.login_fname || values.username}`);
      navigate('/partner-portal/dashboard', { replace: true });
    } catch (err) {
      ToastMsg('error', err?.message || 'Login failed');
    }
  };

  const yearNow = useMemo(() => new Date().getFullYear(), []);

  return (
    <div className="min-h-screen relative overflow-hidden bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4 sm:p-6">
      {/* Ambient background — soft mesh */}
      <div className="pointer-events-none absolute inset-0 -z-0">
        <div className="absolute -top-32 -left-32 w-[26rem] h-[26rem] rounded-full bg-emerald-200/40 dark:bg-emerald-500/10 blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-[26rem] h-[26rem] rounded-full bg-teal-200/40 dark:bg-teal-500/10 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[36rem] h-[36rem] rounded-full bg-cyan-100/30 dark:bg-cyan-500/5 blur-3xl" />
      </div>

      <div className="relative w-full max-w-5xl grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] rounded-2xl overflow-hidden shadow-xl border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-900">

        {/* ── Left — Value panel ────────────────────────────────────────── */}
        <div className="relative hidden lg:flex flex-col p-8 xl:p-10 bg-gradient-to-br from-emerald-600 via-emerald-600 to-teal-700 text-white">
          {/* Decorative overlay */}
          <div className="absolute inset-0 opacity-[0.15] mix-blend-overlay"
               style={{ backgroundImage: "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.5), transparent 40%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.35), transparent 45%)" }} />

          <div className="relative flex flex-col h-full">
            {/* Brand mark */}
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-white/15 backdrop-blur-sm border border-white/25 flex items-center justify-center shadow-sm">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-emerald-100/80 font-semibold">Hospital</div>
                <div className="text-base font-semibold tracking-tight -mt-0.5">Partner Portal</div>
              </div>
            </div>

            {/* Hero copy */}
            <div className="mt-10 space-y-3">
              <h1 className="text-3xl xl:text-[2rem] font-bold leading-[1.15] tracking-tight">
                Refer patients.<br />
                <span className="text-emerald-100">Track outcomes.</span> Get paid.
              </h1>
              <p className="text-emerald-50/90 text-sm max-w-md leading-relaxed">
                A dedicated workspace for referring doctors and ambulance partners.
                Send a patient in seconds, watch every conversion, know what you've earned.
              </p>
            </div>

            {/* Benefits */}
            <ul className="mt-8 space-y-3.5 max-w-md">
              {BENEFITS.map(b => (
                <li key={b.title} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/12 border border-white/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <b.icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium leading-tight">{b.title}</div>
                    <div className="text-[12px] text-emerald-50/80 leading-snug">{b.text}</div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="flex-1" />

            {/* Partner-type strip */}
            <div className="mt-8 pt-4 border-t border-white/15 flex items-center gap-4 text-[11px] text-emerald-50/85">
              <div className="flex items-center gap-1.5"><Stethoscope className="w-3.5 h-3.5" /> Referring doctors</div>
              <div className="w-px h-3.5 bg-white/20" />
              <div className="flex items-center gap-1.5"><Truck className="w-3.5 h-3.5" /> Ambulance partners</div>
            </div>
          </div>
        </div>

        {/* ── Right — Form ─────────────────────────────────────────────── */}
        <div className="relative p-6 sm:p-8 lg:p-10 flex flex-col justify-center">
          {/* Mobile-only brand */}
          <div className="lg:hidden flex items-center gap-2 mb-5 text-emerald-600 dark:text-emerald-400">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white shadow-sm">
              <ShieldCheck className="w-4.5 h-4.5" />
            </div>
            <span className="font-semibold tracking-tight">Partner Portal</span>
          </div>

          <div className="mb-6">
            <h2 className="text-[22px] font-bold text-slate-900 dark:text-white leading-tight tracking-tight">Sign in</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Use the credentials shared by the hospital's referral desk.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <Field label="Organisation code" required error={errors.org_slug} hint="Case-insensitive. e.g. apollo-chennai">
              <InputRow
                inputRef={orgInputRef}
                icon={Building2}
                type="text"
                value={values.org_slug}
                onChange={e => set('org_slug', e.target.value)}
                placeholder="apollo-chennai"
                autoComplete="organization"
                error={!!errors.org_slug}
              />
            </Field>

            <Field label="Username" required error={errors.username}>
              <InputRow
                inputRef={usernameInputRef}
                icon={User}
                type="text"
                value={values.username}
                onChange={e => set('username', e.target.value)}
                placeholder="dr_ramesh_kumar"
                autoComplete="username"
                error={!!errors.username}
              />
            </Field>

            <Field label="Password" required error={errors.password}>
              <InputRow
                icon={Lock}
                type={showPass ? 'text' : 'password'}
                value={values.password}
                onChange={e => set('password', e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                error={!!errors.password}
                rightAdornment={
                  <button
                    type="button"
                    onClick={() => setShowPass(s => !s)}
                    tabIndex={-1}
                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 focus:outline-none"
                    aria-label={showPass ? 'Hide password' : 'Show password'}
                  >
                    {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                }
              />
            </Field>

            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium text-white bg-gradient-to-r from-emerald-500 to-teal-600 shadow-sm hover:from-emerald-600 hover:to-teal-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900 disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
            >
              {loading
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</>
                : <>Sign in</>}
            </button>

            <div className="flex items-start gap-2 text-[11px] text-slate-500 dark:text-slate-400 pt-1">
              <HelpCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>
                Forgot your password? Ask the hospital admin to reset it from your partner profile — a new password will be shared with you.
              </span>
            </div>
          </form>

          {/* Footer */}
          <div className="mt-8 pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-[11px] text-slate-400 dark:text-slate-500">
            <span>© {yearNow} Hospital Partner Portal</span>
            <a href="mailto:support@tecnospice.com" className="inline-flex items-center gap-1 hover:text-slate-600 dark:hover:text-slate-300">
              <Mail className="w-3 h-3" /> Support
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};


function Field({ label, required, error, hint, children }) {
  return (
    <div>
      <label className="block text-[11px] font-medium text-slate-700 dark:text-slate-300 mb-1.5">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      {children}
      {error ? (
        <div className="mt-1 text-[11px] text-rose-500">{error}</div>
      ) : hint ? (
        <div className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">{hint}</div>
      ) : null}
    </div>
  );
}


function InputRow({ icon: Icon, rightAdornment, error, inputRef, ...inputProps }) {
  return (
    <div className={`
      relative flex items-center gap-2 rounded-xl border px-3 py-2
      transition-colors bg-white dark:bg-slate-950/40
      ${error
        ? 'border-rose-300 dark:border-rose-800 focus-within:border-rose-500 focus-within:ring-2 focus-within:ring-rose-200 dark:focus-within:ring-rose-900/40'
        : 'border-slate-200 dark:border-slate-800 focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-100 dark:focus-within:ring-emerald-900/30'}
    `}>
      {Icon && <Icon className={`w-4 h-4 ${error ? 'text-rose-400' : 'text-slate-400 dark:text-slate-500'} flex-shrink-0`} />}
      <input
        ref={inputRef}
        {...inputProps}
        className="flex-1 min-w-0 bg-transparent text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none"
      />
      {rightAdornment}
    </div>
  );
}

export default PartnerPortalSignin;
